import type { CSSProperties } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { INTERAX_COLORS } from '../../../../constants/colors'

// Outside the small floating card, the people's tiles keep this height and scroll rather than shrink, so a call
// of the whole sprint stays readable.
const PEOPLE_TILE_MIN_HEIGHT_PX = 120 // 120 px
const FLOATING_CARD_VIDEO_HEIGHT_PX = 240 // 240 px
// Beside a shared screen the people take a column of about a fifth of the width, as in Google Meet.
const PEOPLE_COLUMN_WIDTH_PERCENT = '22%' // 22 %
const PEOPLE_COLUMN_MIN_WIDTH_PX = 200 // 200 px
const PEOPLE_COLUMN_MAX_WIDTH_PX = 320 // 320 px
const PEOPLE_TILE_ASPECT_RATIO = '16 / 9'
const PEOPLE_TILE_RADIUS_PX = 8 // 8 px, the corners of a tile without video

// Below lg the shared screen sits above the people; from lg up, beside them.
const belowLgThen = <Value,>(belowLg: Value, fromLg: Value) => ({ xs: belowLg, sm: belowLg, md: belowLg, lg: fromLg })

const videoAreaBaseSx = {
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex'
} as const

const stackedVideoAreaSx = { ...videoAreaBaseSx, flexDirection: 'column', gap: 1 } as const

const sharedScreenVideoAreaSx = {
  ...videoAreaBaseSx,
  flexDirection: belowLgThen('column', 'row'),
  gap: belowLgThen(1, 1.5)
} as const

const floatingCardSizeSx = { height: FLOATING_CARD_VIDEO_HEIGHT_PX, flexShrink: 0 }
const pageSizeSx = { flex: 1, minHeight: 0 }

// The video area: the shared screen and the people, side by side from lg up while a screen is shared on the page.
export const buildVideoAreaSx = (compact: boolean, isScreenSharedOnPage: boolean): SxProps<Theme> => {
  if (compact) return { ...stackedVideoAreaSx, ...floatingCardSizeSx }
  if (isScreenSharedOnPage) return { ...sharedScreenVideoAreaSx, ...pageSizeSx }
  return { ...stackedVideoAreaSx, ...pageSizeSx }
}

const floatingCardGridSx = {
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridAutoRows: 'minmax(0, 1fr)',
  overflow: 'hidden'
}

const pageGridSx = {
  gridTemplateColumns: {
    xs: 'repeat(1, minmax(0, 1fr))',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(auto-fit, minmax(220px, 1fr))'
  },
  gridAutoRows: `minmax(${PEOPLE_TILE_MIN_HEIGHT_PX}px, 1fr)`,
  overflowX: 'hidden',
  overflowY: 'auto'
}

// From lg up: one column of small 16:9 tiles on the page itself, scrolling when they outgrow it.
const sharedScreenGridSx = {
  ...pageGridSx,
  flex: belowLgThen('1', '0 0 auto'),
  width: belowLgThen('100%', PEOPLE_COLUMN_WIDTH_PERCENT),
  minWidth: belowLgThen(0, PEOPLE_COLUMN_MIN_WIDTH_PX),
  maxWidth: belowLgThen<string | number>('none', PEOPLE_COLUMN_MAX_WIDTH_PX),
  gridTemplateColumns: { ...pageGridSx.gridTemplateColumns, lg: 'minmax(0, 1fr)' },
  gridAutoRows: belowLgThen(pageGridSx.gridAutoRows, 'max-content'),
  alignContent: belowLgThen('stretch', 'start'),
  p: belowLgThen(1.5, 0),
  border: belowLgThen(`1px solid ${INTERAX_COLORS.white08}`, 'none'),
  backgroundColor: belowLgThen(INTERAX_COLORS.heritageBlue30, 'transparent'),
  '& > *': { aspectRatio: belowLgThen('auto', PEOPLE_TILE_ASPECT_RATIO) },
  '& video': { width: '100%', objectFit: 'cover', borderRadius: belowLgThen('0px', `${PEOPLE_TILE_RADIUS_PX}px`) }
}

const resolvePeopleGridLayoutSx = (compact: boolean, isScreenSharedOnPage: boolean) => {
  if (compact) return floatingCardGridSx
  if (isScreenSharedOnPage) return sharedScreenGridSx
  return pageGridSx
}

// The people's tiles: a grid in the floating card and on the page, a column beside a shared screen from lg up.
export const buildPeopleGridSx = (compact: boolean, isScreenSharedOnPage: boolean): SxProps<Theme> => {
  const layoutSx = resolvePeopleGridLayoutSx(compact, isScreenSharedOnPage)

  return {
    width: '100%',
    flex: 1,
    minHeight: 0,
    borderRadius: '12px',
    border: `1px solid ${INTERAX_COLORS.white08}`,
    backgroundColor: INTERAX_COLORS.heritageBlue30,
    display: 'grid',
    gap: 1.5,
    '& video': { width: '100%', height: '100%', objectFit: 'cover' },
    p: 1.5,
    boxSizing: 'border-box',
    ...layoutSx
  }
}

// Stacked, a shared screen takes three parts of the height to the people's one; beside the people it takes the rest.
const SCREENSHARE_STAGE_FLEX = 3

// A shared screen is shown whole, never cropped like a face; the video takes its fit from its own style.
export const SCREENSHARE_VIDEO_STYLE: CSSProperties = { objectFit: 'contain' }

export const screenshareStageSx: SxProps<Theme> = {
  flex: SCREENSHARE_STAGE_FLEX,
  minHeight: 0,
  minWidth: 0,
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: 'minmax(0, 1fr)',
  gap: 1,
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: INTERAX_COLORS.black55,
  '& video': { width: '100%', height: '100%' }
}
